/**
 * Drawing Magnet and Crosshair Snapping Geometry.
 *
 * Compares candidates by 2D on-screen pixel distance (dx^2 + dy^2), ensuring
 * points snap to the visually nearest OHLC vertex rather than mis-selecting
 * distant neighboring bars.
 */

export const MAGNET_MODES = ["off", "weak", "strong"] as const;
export type MagnetMode = typeof MAGNET_MODES[number];

export const WEAK_MAGNET_SNAP_PX = 18;

export interface Point2D {
  x: number;
  y: number;
}

export interface CandleOhlc {
  time: any;
  open?: number | null;
  high?: number | null;
  low?: number | null;
  close?: number | null;
}

export interface OhlcSnapResult {
  price: number | null;
  time: any;
  snapped: boolean;
  candle?: CandleOhlc;
  distPx?: number;
  vertex?: "open" | "high" | "low" | "close";
}

/**
 * Cycles magnet mode to the next option in sequence: off -> weak -> strong -> off.
 */
export function cycleMagnetMode(currentMode: string): MagnetMode {
  const idx = MAGNET_MODES.indexOf(currentMode as MagnetMode);
  if (idx < 0) return "off";
  return MAGNET_MODES[(idx + 1) % MAGNET_MODES.length];
}

/**
 * Parses persisted magnet mode setting with legacy migration fallback.
 */
export function parseMagnetMode(
  storedValue: string | null,
  legacyValue?: string | null,
): MagnetMode {
  if (storedValue && (MAGNET_MODES as readonly string[]).includes(storedValue)) {
    return storedValue as MagnetMode;
  }
  if (legacyValue === "true") return "strong";
  if (legacyValue === "false") return "off";
  return "off";
}

/**
 * Snaps cursor coordinate to the nearest OHLC vertex using 2D Euclidean distance,
 * returning both the snapped price AND the exact candle time for that vertex.
 */
export function findNearestOhlcPoint(
  point: Point2D,
  candles: CandleOhlc[],
  timeToCoordinate: (time: any) => number | null,
  priceToCoordinate: (price: number) => number | null,
  mode: MagnetMode,
  rawPrice: number | null,
  rawTime: any = null,
  weakSnapPx: number = WEAK_MAGNET_SNAP_PX,
): OhlcSnapResult {
  if (mode === "off" || !Array.isArray(candles) || candles.length === 0) {
    return { price: rawPrice, time: rawTime, snapped: false };
  }

  if (rawPrice === null || isNaN(rawPrice)) {
    return { price: null, time: rawTime, snapped: false };
  }

  let nearestPrice: number | null = null;
  let nearestTime: any = null;
  let nearestCandle: CandleOhlc | undefined = undefined;
  let nearestVertex: "open" | "high" | "low" | "close" | undefined = undefined;
  let nearestDistSq = Infinity;

  const vertices: Array<"high" | "low" | "open" | "close"> = ["high", "low", "open", "close"];

  for (const candle of candles) {
    if (!candle || candle.time == null) continue;
    const cx = timeToCoordinate(candle.time);
    if (cx === null || isNaN(cx)) continue;
    const dx = cx - point.x;

    for (const key of vertices) {
      const v = candle[key];
      if (v == null || isNaN(v)) continue;
      const cy = priceToCoordinate(v);
      if (cy === null || isNaN(cy)) continue;
      const dy = cy - point.y;
      const distSq = dx * dx + dy * dy;

      if (distSq < nearestDistSq) {
        nearestDistSq = distSq;
        nearestPrice = v;
        nearestTime = candle.time;
        nearestCandle = candle;
        nearestVertex = key;
      }
    }
  }

  if (nearestPrice === null || nearestTime === null) {
    return { price: rawPrice, time: rawTime, snapped: false };
  }

  const distPx = Math.sqrt(nearestDistSq);

  if (mode === "weak" && distPx > weakSnapPx) {
    return { price: rawPrice, time: rawTime, snapped: false };
  }

  return {
    price: nearestPrice,
    time: nearestTime,
    snapped: true,
    candle: nearestCandle,
    distPx,
    vertex: nearestVertex,
  };
}

/**
 * Snaps cursor coordinate to the nearest OHLC value using Euclidean pixel distance.
 * Backwards-compatible wrapper around findNearestOhlcPoint.
 */
export function findNearestOhlcPrice(
  point: Point2D,
  candles: CandleOhlc[],
  timeToCoordinate: (time: any) => number | null,
  priceToCoordinate: (price: number) => number | null,
  mode: MagnetMode,
  rawPrice: number | null,
  weakSnapPx: number = WEAK_MAGNET_SNAP_PX,
): number | null {
  return findNearestOhlcPoint(
    point,
    candles,
    timeToCoordinate,
    priceToCoordinate,
    mode,
    rawPrice,
    null,
    weakSnapPx,
  ).price;
}
